import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import MenuItemForm from '../components/admin/menu/MenuItemForm';
import SuccessToast from '../components/common/SuccessToast';
import './AddMenuItem.css';

const AddMenuItem = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories
        setCategories(['Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts']);
        return;
      }

      if (data && data.length > 0) {
        const categoryNames = data.map(cat => cat.name);
        setCategories(categoryNames);
      } else {
        // If no categories exist, use defaults
        setCategories(['Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts']);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts']);
    }
  };

  const handleSave = async (itemData) => {
    setSaving(true);
    try {
      // Prepare data for Supabase
      const menuItemData = {
        name: itemData.name,
        category: itemData.category,
        description: itemData.description,
        price: itemData.price,
        cooking_time: itemData.cookingTime,
        images_urls: itemData.images,
        tags: itemData.tags,
        customizations: itemData.customizations,
        is_available: itemData.isAvailable,
        is_featured: itemData.isFeatured,
        is_popular: itemData.isPopular || false,
        is_new: itemData.isNew || false
      };

      // Check if Supabase is configured
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - just show success
        console.log('Demo mode: Menu item data:', menuItemData);
        setSuccessMessage('Menu item saved successfully! (Demo mode)');
        setShowSuccessToast(true);
        setTimeout(() => {
          navigate('/admin/menu');
        }, 2000);
        return;
      }

      // Save to Supabase
      const { data, error } = await supabase
        .from('menu_items')
        .insert([menuItemData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      setSuccessMessage('Menu item saved successfully!');
      setShowSuccessToast(true);
      setTimeout(() => {
        navigate('/admin/menu');
      }, 2000);
    } catch (error) {
      console.error('Error saving menu item:', error);
      setSuccessMessage('Failed to save menu item. Please try again.');
      setShowSuccessToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/menu');
  };

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="add-menu-item-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="add-menu-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="add-menu-content">
          <div className="page-header">
            <div>
              <h1 className="page-title">Add New Menu Item</h1>
              <nav className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">/</span>
                <span>Menu</span>
                <span className="breadcrumb-separator">/</span>
                <span>Add Item</span>
              </nav>
            </div>
            <button className="cancel-header-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>

          <div className="form-container">
            <MenuItemForm
              categories={categories}
              onSave={handleSave}
              onCancel={handleCancel}
              saving={saving}
            />
          </div>
        </main>
      </div>
      
      {showSuccessToast && (
        <SuccessToast
          message={successMessage}
          onClose={() => setShowSuccessToast(false)}
          duration={3000}
          type={successMessage.includes('Failed') ? 'error' : 'success'}
        />
      )}
    </div>
  );
};

export default AddMenuItem;

